/**
 * Which NFL club a man in the box-score archive played for, that season.
 *
 * THE ARCHIVE DOES NOT KNOW. Every line in it is `[name, slot, points]` — a Yahoo scrape with no
 * club anywhere in it — which is why the team chips the draft board and the keeper screens carry
 * stopped at the edge of History. This closes that: `build-box-clubs.mjs` matches the archive's
 * interned name table against nflverse's per-season roster files and writes `box-clubs.json`.
 *
 * PER SEASON, WHICH IS THE ENTIRE POINT. Reading the club off the current player index would have
 * been one line and would have printed LV beside a 2009 Raider, CIN beside 2011 Chad Ochocinco,
 * and LAR beside a 2010 St. Louis Ram — silently, on the one page whose job is to be the record.
 * A 2010 Ram gets STL here. The relocations are kept because they are facts about the season.
 *
 * NULL IS A REAL ANSWER. About one line in a hundred cannot be resolved: two Alex Smiths, two
 * Steve Smiths, two Chad Johnsons on rosters at once. The generator refuses those rather than
 * guessing, so this returns null and the screen draws no chip. A gap is a gap; a wrong chip is
 * the app lying about the record.
 */

import raw from '../data/box-clubs.json';

interface ClubFile {
  version: number;
  /** Size and fingerprint of the name table this was built against — see `fingerprintOf`. */
  names: number;
  fingerprint: number;
  source: string;
  built: string;
  clubs: string[];
  /** season → flat pairs of [name index, club index]. */
  by: Record<string, number[]>;
}

const FILE = raw as unknown as ClubFile;

/**
 * Built lazily and per season, because History opens on a list of games and most visits never
 * expand one. A season is a few hundred entries; the whole file is sixteen thousand.
 */
const cache = new Map<number, Map<number, string>>();

function seasonMap(season: number): Map<number, string> {
  const had = cache.get(season);
  if (had) return had;
  const flat = FILE.by[String(season)] ?? [];
  const m = new Map<number, string>();
  for (let i = 0; i < flat.length; i += 2) {
    const club = FILE.clubs[flat[i + 1] as number];
    if (club) m.set(flat[i] as number, club);
  }
  cache.set(season, m);
  return m;
}

/**
 * The lookup the archive's own name table provides.
 *
 * Keyed by the RAW interned string — the one with Yahoo's icon glyph and `Final W 24-19 vs LV`
 * still on it — rather than by the cleaned display name, because the raw string is what the
 * generator indexed and it is one-to-one with a name index. Cleaning at both ends would be two
 * copies of the same regexes waiting to disagree.
 */
let byName: Map<string, number> | null = null;
let usable: boolean | null = null;

/** FNV-1a over the whole name table, the same walk the generator does. */
export function fingerprintOf(names: readonly string[]): number {
  let h = 2166136261;
  for (const n of names) {
    for (let i = 0; i < n.length; i += 1) {
      h ^= n.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
  }
  return h >>> 0;
}

/**
 * Whether this file describes the archive that is actually loaded.
 *
 * The mapping is by NAME INDEX, so an archive re-scraped in a different order would not make the
 * chips MISSING — it would make every one of them wrong, quietly, on the page whose whole job is
 * to be the record. Checked once, and a mismatch means no chips at all.
 */
export function matches(names: readonly string[]): boolean {
  if (usable === null) {
    usable = names.length === FILE.names && fingerprintOf(names) === FILE.fingerprint;
  }
  return usable;
}

export function clubFor(season: number, rawName: string, names: readonly string[]): string | null {
  if (!matches(names)) return null;
  if (!byName) byName = new Map(names.map((n, i) => [n, i]));
  const ix = byName.get(rawName);
  if (ix === undefined) return null;
  return seasonMap(season).get(ix) ?? null;
}

export const clubSource = (): string => FILE.source;
export const clubCount = (): number =>
  Object.values(FILE.by).reduce((n, flat) => n + flat.length / 2, 0);
